import { Struct, Value } from "@bufbuild/protobuf";
import { format } from "date-fns";
import { type Setter, atom } from "jotai";

import { type Json, serializeForStruct, tldrawRoomId } from "@moment/api-collab";
import type { DocumentMetadata } from "@moment/api-collab/api-types";
import { parsePage } from "@moment/api-collab/markdown";
import { Logger } from "@moment/logging";

import { $accessTokenFunc } from "../../authenication";
import { LOCAL_WORKSPACE_NAME } from "../../constants";
import { $docsApi } from "../../docsapi/docsapi.atom";
import { $collabSessionsStore } from "../../document-collab-registry";
import { $addDraftDocument, $document, $documentLocalPath } from "../../document-list";
import { $readAgentsMdDocFromDisk } from "../../document-loader/load-agents-md-collab.atom";
import { metadataKeyForDocsRoute } from "../../document-metadata/documentMetadataKey";
import { $documentMetadata } from "../../document-metadata/metadata-state.atom";
import { env, getTldrawBaseUrl } from "../../env/env";
import { readTextFile } from "../../filesystem";
import {
	$gitBranchSwitch,
	$gitBranchTrack,
	$gitCommit,
	$gitFetch,
	$gitPush,
	$gitRebaseBookmark,
	$gitRemoteAdd,
	$gitStatus,
} from "../../git/git-repository.atom";
import { $git } from "../../git/git.atom";
import {
	$pageCanvasSnapshot,
	canvasSnapshotKeyForDocsRoute,
} from "../../page/page-canvas-state.atom";
import { getPageFilePath } from "../../paths/paths.internal";
import { $editorState } from "../../prosemirror";
import { editorStateKeyForDocsRoute } from "../../prosemirror/editorStateKey";
import { $collabRegistryConnection } from "../../reboot/collab-registry-connection.atom";
import { $documentStoreV2 } from "../../reboot/document-store-v2.atom";
import { $httpRequestPage } from "../../request-page/request-page.atom";
import { requestPageKeyForDocsRoute } from "../../request-page/requestPageKey";
import { type Workspace } from "../../workspaces";
import { PRIMARY_AGENTS_MD_PAGE_ID } from "../agents-md.atom";
import { NoPagesFoundError } from "./errors";

const logger = new Logger("sync/mutations.atom");

// Git constants
export const GIT_PUBLISHING_BRANCH_NAME = "main";
export const GIT_COLLAB_BRANCH = "collab/main";
export const GIT_PUBLISHING_REMOTE_NAME = "moment";

export const initialCommit = async (
	set: Setter,
	input: { documentId: string; dryRun?: boolean }
): Promise<string | undefined> => {
	if (input.dryRun) {
		return undefined;
	}
	const defaultMessage = `🚀 Initial commit at ${format(new Date(), "yyyy/MM/dd")}`;
	return set($gitCommit(input.documentId), { message: defaultMessage });
};

/**
 * Commit changes
 */
export const $commitChanges = atom(
	null,
	async (
		_get,
		set,
		input: { documentId: string; message?: string; dryRun?: boolean }
	): Promise<string | undefined> => {
		if (input.dryRun) {
			return undefined;
		}
		const defaultMessage = `Publishing changes ${format(new Date(), "yyyy/MM/dd")}`;
		const commitMessage = input.message ?? defaultMessage;

		const commitAtom = $gitCommit(input.documentId);
		return set(commitAtom, { message: commitMessage });
	}
);
$commitChanges.debugLabel = `commitChanges`;

/**
 * Initialize git repository
 */
export const $initializeGitRepo = atom(
	null,
	async (get, _set, params: { documentId: string; dryRun?: boolean }) => {
		if (params.dryRun) {
			return;
		}
		const path = await get($documentLocalPath(params.documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);
		await git.init({ path });
	}
);
$initializeGitRepo.debugLabel = `initializeGitRepo`;

/**
 * Create moment remote
 */
export const $createRemote = atom(
	null,
	async (_get, set, params: { documentId: string; workspace: Workspace; dryRun?: boolean }) => {
		if (params.dryRun) {
			return;
		}
		const { documentId, workspace } = params;
		if (!workspace || !workspace.name || workspace.name === LOCAL_WORKSPACE_NAME) {
			throw new Error("Invalid workspace for creating remote");
		}

		const docsApiUrl = env.docsApiUrl;
		if (!docsApiUrl) {
			throw new Error("DocsAPI URL not available");
		}

		const remoteUrl = `${docsApiUrl}/v2/git/${workspace.name}/${documentId}.git`;

		try {
			const remoteAddAtom = $gitRemoteAdd(documentId);
			await set(remoteAddAtom, { remote: GIT_PUBLISHING_REMOTE_NAME, url: remoteUrl });
			return remoteUrl;
		} catch (error) {
			// If remote already exists, that's okay
			if (
				error instanceof Error &&
				error.message.includes("remote 'moment' already exists")
			) {
				logger.warn("Remote already exists, continuing");
				return;
			}
			throw error;
		}
	}
);
$createRemote.debugLabel = `createRemote`;

/**
 * Set tracking branch for main
 */
export const $setTrackingBranch = atom(
	null,
	async (_get, set, params: { documentId: string; dryRun?: boolean }) => {
		if (params.dryRun) {
			return;
		}
		const branchTrackAtom = $gitBranchTrack;
		await set(branchTrackAtom, {
			input: {
				remote: GIT_PUBLISHING_REMOTE_NAME,
				localBranch: GIT_PUBLISHING_BRANCH_NAME,
				upstreamBranch: GIT_PUBLISHING_BRANCH_NAME,
			},
			documentId: params.documentId,
		});
	}
);
$setTrackingBranch.debugLabel = "setTrackingBranch";

/**
 * Switch to collab branch
 */
export const $switchToCollabBranch = atom(null, async (_get, set, documentId: string) => {
	await set($gitBranchSwitch, {
		documentId,
		input: {
			target: GIT_COLLAB_BRANCH,
		},
	});
});
$switchToCollabBranch.debugLabel = "switchToCollabBranch";

/**
 * Push to remote
 */
export const $pushToRemote = atom(
	null,
	async (_get, set, params: { documentId: string; sourceCommit?: string; dryRun?: boolean }) => {
		if (params.dryRun) {
			return;
		}
		logger.debug("Pushing to remote", {
			documentId: params.documentId,
			remote: GIT_PUBLISHING_REMOTE_NAME,
			branch: GIT_PUBLISHING_BRANCH_NAME,
			sourceCommit: params.sourceCommit,
		});
		await set($gitPush, {
			documentId: params.documentId,
			input: {
				remote: GIT_PUBLISHING_REMOTE_NAME,
				branch: GIT_PUBLISHING_BRANCH_NAME,
				sourceCommit: params.sourceCommit,
			},
		});
		logger.debug("Push to remote completed", {
			documentId: params.documentId,
			branch: GIT_PUBLISHING_BRANCH_NAME,
		});
	}
);
$pushToRemote.debugLabel = "pushToRemote";

export const $gitFetchPublish = (set: Setter, documentId: string) => {
	return set($gitFetch, {
		documentId,
		input: {
			remote: GIT_PUBLISHING_REMOTE_NAME,
			branch: GIT_PUBLISHING_BRANCH_NAME,
		},
	});
};

/**
 * Create collab branch
 */
export const $createCollabBranch = atom(null, async (get, _set, documentId: string) => {
	const path = await get($documentLocalPath(documentId));
	if (!path) {
		logger.error("Document path not found for creating collab branch", { documentId });
		throw new Error("Document path not found");
	}

	const git = get($git);
	await git.branchNew({ path, name: GIT_COLLAB_BRANCH });
});
$createCollabBranch.debugLabel = "createCollabBranch";

/**
 * Push collab branch
 */
export const $pushCollabBranch = atom(
	null,
	async (_get, set, params: { documentId: string; sourceCommit?: string }) => {
		await set($gitPush, {
			documentId: params.documentId,
			input: {
				remote: GIT_PUBLISHING_REMOTE_NAME,
				branch: GIT_COLLAB_BRANCH,
				sourceCommit: params.sourceCommit,
			},
		});
	}
);
$pushCollabBranch.debugLabel = "pushCollabBranch";

/**
 * Switch to main branch
 */
export const $switchToMainBranch = atom(null, async (_get, set, documentId: string) => {
	await set($gitBranchSwitch, {
		documentId,
		input: {
			target: GIT_PUBLISHING_BRANCH_NAME,
		},
	});
});
$switchToMainBranch.debugLabel = "switchToMainBranch";

/**
 * Rebase main bookmark to collab/main via jj rebase
 */
export const $mergeCollabToMain = atom(null, async (_get, set, documentId: string) => {
	await set($gitRebaseBookmark, {
		documentId,
		localBranch: GIT_PUBLISHING_BRANCH_NAME,
		targetRef: `refs/heads/${GIT_COLLAB_BRANCH}`,
	});
});
$mergeCollabToMain.debugLabel = "mergeCollabToMain";

/**
 * Sync main bookmark to collab/main's described commit via jj.
 *
 * This moves the main bookmark to point to the same described commit
 * that collab/main points to, and exports git refs. No branch switch
 * or git2 merge needed — stays on the current branch.
 *
 * Returns the described commit hash that main now points to.
 */
export const $syncMainToCollab = atom(
	null,
	async (get, _set, documentId: string): Promise<string> => {
		const path = await get($documentLocalPath(documentId));
		if (!path) {
			throw new Error("Document path not found");
		}

		const git = get($git);
		return git.syncBranch({
			path,
			sourceBranch: GIT_COLLAB_BRANCH,
			targetBranch: GIT_PUBLISHING_BRANCH_NAME,
		});
	}
);
$syncMainToCollab.debugLabel = "syncMainToCollab";

/**
 * Set collab tracking branch
 */
export const $setCollabTrackingBranch = atom(null, async (_get, set, documentId: string) => {
	await set($gitBranchTrack, {
		documentId,
		input: {
			remote: GIT_PUBLISHING_REMOTE_NAME,
			localBranch: GIT_COLLAB_BRANCH,
			upstreamBranch: GIT_COLLAB_BRANCH,
		},
	});
});
$setCollabTrackingBranch.debugLabel = "setCollabTrackingBranch";

/**
 * Create publish repo on remote
 */
export const $createPublishRepo = atom(
	null,
	async (get, _set, params: { documentId: string; workspace: Workspace; dryRun?: boolean }) => {
		if (params.dryRun) {
			return;
		}
		const { documentId, workspace } = params;
		logger.debug("Creating remote repo for publishing", { workspace, documentId });
		if (!workspace?.name || workspace.name === LOCAL_WORKSPACE_NAME) {
			logger.error("Invalid workspace for creating remote repo", { workspace });
			throw new Error("Invalid workspace for creating remote repo");
		}

		const docsApi = get($docsApi);
		const { getAccessTokenSilently } = get($accessTokenFunc);
		if (!getAccessTokenSilently) {
			logger.error("Authentication function not available. Cannot create remote repo", {
				workspace,
				documentId,
			});
			throw new Error("Authentication function not available");
		}
		const token = await getAccessTokenSilently();
		if (!token) {
			logger.error("Authentication token not available. Cannot create remote repo", {
				workspace,
				documentId,
			});
			throw new Error("Authentication token not available");
		}
		// in staging and development, use v2 endpoint
		await docsApi.document.remote.create(workspace.name!, documentId, token);
	}
);
$createPublishRepo.debugLabel = `createPublishRepo`;

/**
 * Publish document (grant read permission to everyone)
 */
export const $publishDocument = atom(
	null,
	async (get, _set, params: { documentId: string; workspace: Workspace }) => {
		const { documentId, workspace } = params;
		logger.debug("Publishing document", { workspace, documentId });
		if (!workspace || !workspace.name || workspace.name === LOCAL_WORKSPACE_NAME) {
			logger.error("Invalid workspace for publishing document", { workspace });
			throw new Error("Invalid workspace for publishing document");
		}

		const docsApi = get($docsApi);
		const { getAccessTokenSilently } = get($accessTokenFunc);
		if (!getAccessTokenSilently) {
			logger.error("Authentication function not available. Cannot publish document", {
				workspace,
				documentId,
			});
			throw new Error("Authentication function not available");
		}
		const token = await getAccessTokenSilently();
		if (!token) {
			logger.error("Authentication token not available. Cannot publish document", {
				workspace,
				documentId,
			});
			throw new Error("Authentication token not available");
		}

		await docsApi.document.permissions.updatePublicAccess(
			{
				workspaceName: workspace.name,
				documentId,
				type: "grant",
				relation: "reader",
				loggedInUsersOnly: false,
			},
			token
		);
	}
);
$publishDocument.debugLabel = `publishDocument`;

/**
 * Unpublish document (revoke read permission from everyone)
 */
export const $unpublishDocument = atom(
	null,
	async (get, _set, params: { documentId: string; workspace: Workspace }) => {
		const { documentId, workspace } = params;
		if (!workspace || !workspace.name || workspace.name === LOCAL_WORKSPACE_NAME) {
			logger.error("Invalid workspace for unpublishing document", { workspace });
			throw new Error("Invalid workspace for unpublishing document");
		}

		const docsApi = get($docsApi);
		const { getAccessTokenSilently } = get($accessTokenFunc);
		if (!getAccessTokenSilently) {
			logger.error("Authentication function not available. Cannot unpublish document", {
				workspace,
				documentId,
			});
			throw new Error("Authentication function not available");
		}
		const token = await getAccessTokenSilently();
		if (!token) {
			logger.error("Authentication token not available. Cannot unpublish document", {
				workspace,
				documentId,
			});
			throw new Error("Authentication token not available");
		}

		await docsApi.document.permissions.updatePublicAccess(
			{
				workspaceName: workspace.name,
				documentId,
				type: "revoke",
				relation: "reader",
				loggedInUsersOnly: false,
			},
			token
		);
	}
);
$unpublishDocument.debugLabel = "unpublishDocument";

/**
 * Provision publish URL (wait for remote document to be available)
 */
export const $verifyRemoteExists = atom(
	null,
	async (get, _set, params: { documentId: string; workspace: Workspace; dryRun?: boolean }) => {
		logger.debug("Verifying remote document exists", { params });
		if (params.dryRun) {
			return;
		}
		const { documentId, workspace } = params;
		if (!workspace || !workspace.name) {
			logger.error("Workspace not found for provisioning URL", { workspace });
			throw new Error("Workspace not found for provisioning URL");
		}

		const docsApi = get($docsApi);
		const maxRetries = 3;
		let lastError: Error | undefined;

		for (let attempt = 0; attempt < maxRetries; attempt++) {
			try {
				const { getAccessTokenSilently } = get($accessTokenFunc);
				if (!getAccessTokenSilently) {
					throw new Error("Authentication function not available");
				}
				const token = await getAccessTokenSilently();
				if (!token) {
					throw new Error("Authentication token not available");
				}

				logger.debug("Checking remote document exists", {
					attempt: attempt + 1,
					maxRetries,
					workspaceName: workspace.name,
					documentId,
				});

				const exists = await docsApi.document.remote.exists(
					workspace.name,
					documentId,
					token
				);

				logger.debug("Remote document exists check result", {
					attempt: attempt + 1,
					exists,
					workspaceName: workspace.name,
					documentId,
				});

				if (!exists) {
					throw new Error(
						`Remote document does not exist for workspace ${workspace.name} and document ${documentId}`
					);
				}
				return; // Success
			} catch (error) {
				lastError = error as Error;
				logger.warn("Remote document exists check failed", {
					attempt: attempt + 1,
					maxRetries,
					documentId,
					error: (error as Error).message,
				});

				if (attempt < maxRetries - 1) {
					// Exponential backoff: 1s, 2s, 4s
					const backoffMs = Math.pow(2, attempt) * 1000;
					logger.debug("Retrying after backoff", { backoffMs });
					await new Promise((resolve) => setTimeout(resolve, backoffMs));
				}
			}
		}

		throw lastError;
	}
);
$verifyRemoteExists.debugLabel = "verifyRemoteExists";

/**
 * A pre-captured snapshot of document state for collab initialization.
 *
 * When the share-collab workflow captures this snapshot before the branch switch, it prevents
 * a race condition where `$switchToCollabBranch` (or other operations) could purge the in-memory
 * state, leaving `$initializeCollab` unable to find the metadata/editor states it needs.
 */
export interface AttachedTableSnapshot {
	table: string;
	primaryKey: string;
	rows: Json[];
}

export interface CollabInitSnapshot {
	metadata: DocumentMetadata;
	pageEditorStates: Record<string, Json>;
	requestPagesData: Record<string, Value>;
	canvasSnapshots: Array<{ pageId: string; snapshot: unknown }>;
	attachedTables: AttachedTableSnapshot[];
}

/**
 * Captures the current document metadata and page states as a snapshot for collab initialization.
 * Must be called while the document state is still loaded (before any branch switch or purge).
 *
 * Async because it may need to read AGENTS.md from disk if the editor state
 * hasn't been lazily loaded yet.
 */
export const $captureCollabInitSnapshot = atom(
	null,
	async (get, set, params: { documentId: string }): Promise<CollabInitSnapshot | null> => {
		const metadataKey = metadataKeyForDocsRoute(params.documentId);
		const metadata = get($documentMetadata(metadataKey));
		if (!metadata) {
			return null;
		}

		const pagesMetadata = metadata.pagesMetadata;
		const pageEditorStates: Record<string, Json> = {};
		const requestPagesData: Record<string, Value> = {};
		const canvasSnapshots: Array<{ pageId: string; snapshot: unknown }> = [];

		// For large documents with lazy loading, most editor states won't be
		// in memory. Read unloaded markdown pages from disk so they're included
		// in the collab snapshot.
		const documentPath = await get($documentLocalPath(params.documentId));
		const unloadedMarkdownPages: Array<{ id: string; path?: string }> = [];

		for (const page of pagesMetadata ?? []) {
			switch (page.type) {
				case "markdown":
				case undefined: {
					const editorStateKey = editorStateKeyForDocsRoute({
						documentId: params.documentId,
						pageId: page.id,
					});
					const editorState = get($editorState(editorStateKey));
					if (editorState) {
						pageEditorStates[page.id] = editorState.doc.toJSON();
					} else {
						unloadedMarkdownPages.push({ id: page.id, path: page.path });
					}
					break;
				}

				case "request": {
					const requestKey = requestPageKeyForDocsRoute({
						documentId: params.documentId,
						pageId: page.id,
					});
					const requestPageRecord = get($httpRequestPage(requestKey));
					if (requestPageRecord?.request) {
						requestPagesData[page.id] = Value.fromJson(requestPageRecord.request);
					} else {
						logger.warn("Request page data not found for page during snapshot", {
							pageId: page.id,
						});
					}
					break;
				}

				case "canvas": {
					const canvasKey = canvasSnapshotKeyForDocsRoute({
						documentId: params.documentId,
						pageId: page.id,
					});
					const snapshot = get($pageCanvasSnapshot(canvasKey));
					if (snapshot) {
						canvasSnapshots.push({ pageId: page.id, snapshot });
					} else {
						logger.debug("No canvas snapshot found for page during snapshot", {
							pageId: page.id,
						});
					}
					break;
				}
			}
		}

		// Read unloaded markdown pages from disk
		if (unloadedMarkdownPages.length > 0 && documentPath) {
			logger.debug("Reading unloaded markdown pages from disk for snapshot", {
				documentId: params.documentId,
				count: unloadedMarkdownPages.length,
			});

			await Promise.all(
				unloadedMarkdownPages.map(async (page) => {
					try {
						const filePath = await getPageFilePath(documentPath, page.id, page.path);
						const content = await readTextFile(filePath);
						const { doc } = parsePage(content);
						pageEditorStates[page.id] = doc;
					} catch (err) {
						logger.debug("Failed to read page from disk during snapshot", {
							pageId: page.id,
							err,
						});
					}
				})
			);
		}

		// Include root-level AGENTS.md as a standalone page authority
		// (not in pagesMetadata). Only one copy — CLAUDE.md is disk-only.
		// Try the in-memory editor state first; fall back to reading from disk
		// since AGENTS.md is lazily loaded and may not be in memory yet.
		const agentsMdEditorKey = editorStateKeyForDocsRoute({
			documentId: params.documentId,
			pageId: PRIMARY_AGENTS_MD_PAGE_ID,
		});
		const agentsMdEditorState = get($editorState(agentsMdEditorKey));
		if (agentsMdEditorState) {
			pageEditorStates[PRIMARY_AGENTS_MD_PAGE_ID] = agentsMdEditorState.doc.toJSON();
			logger.debug("AGENTS.md included in snapshot from editor state", {
				documentId: params.documentId,
				pageId: PRIMARY_AGENTS_MD_PAGE_ID,
			});
		} else {
			// Editor state not loaded — read from disk
			const documentPath = await get($documentLocalPath(params.documentId));
			logger.debug("AGENTS.md editor state not loaded, trying disk", {
				documentId: params.documentId,
				documentPath,
			});
			if (documentPath) {
				const diskResult = await set($readAgentsMdDocFromDisk, documentPath);
				if (diskResult) {
					pageEditorStates[diskResult.pageId] = diskResult.doc;
					logger.debug("AGENTS.md included in snapshot from disk", {
						documentId: params.documentId,
						pageId: diskResult.pageId,
					});
				} else {
					logger.debug("AGENTS.md not found on disk", {
						documentId: params.documentId,
					});
				}
			}
		}

		// Snapshot attached tables from the local v2 Store.
		const attachedTables: AttachedTableSnapshot[] = [];
		const localStore = await get($documentStoreV2(params.documentId));
		if (localStore) {
			for (const tableName of localStore.tableNames()) {
				const schema = localStore.tableSchema(tableName);
				if (schema) {
					attachedTables.push({
						table: tableName,
						primaryKey: schema.primaryKey,
						rows: localStore.merged(tableName) as Json[],
					});
				}
			}
		}

		logger.debug("Collab init snapshot captured", {
			documentId: params.documentId,
			pageCount: Object.keys(pageEditorStates).length,
			pageIds: Object.keys(pageEditorStates),
			hasAgentsMd: PRIMARY_AGENTS_MD_PAGE_ID in pageEditorStates,
			attachedTableCount: attachedTables.length,
		});

		return { metadata, pageEditorStates, requestPagesData, canvasSnapshots, attachedTables };
	}
);
$captureCollabInitSnapshot.debugLabel = "captureCollabInitSnapshot";

/**
 * Initialize collab session.
 *
 * Requires a pre-captured `snapshot` containing metadata and page states. The snapshot must be
 * captured before any branch switch or purge — this guarantees `$initializeCollab` always has the
 * data it needs regardless of what reactive effects run during the share workflow.
 */
export const $initializeCollab = atom(
	null,
	async (
		get,
		set,
		params: { documentId: string; workspace: Workspace; snapshot: CollabInitSnapshot }
	) => {
		const { metadata, pageEditorStates, requestPagesData, canvasSnapshots, attachedTables } =
			params.snapshot;
		const reboot = get($collabRegistryConnection(params.workspace.slug))!;
		const gitStatus = await get($gitStatus(params.documentId));
		if (!gitStatus) {
			logger.error("Git status not found for document", { documentId: params.documentId });
			throw new Error("Git status not found for document");
		}
		const sha = gitStatus.commit;
		const branch = gitStatus.branch;

		// Validate that we have at least one page to initialize collab states for
		// You cannot have a document with no pages
		const sumOfPages =
			Object.keys(pageEditorStates).length +
			Object.keys(requestPagesData).length +
			canvasSnapshots.length;
		if (sumOfPages === 0) {
			logger.error("NO PAGES FOUND TO COLLAB SHARE. THIS SHOULD NOT HAPPEN.");
			throw new NoPagesFoundError("share-collab");
		}

		logger.debug("Initializing collab states", {
			documentId: params.documentId,
			branch,
			sha,
			pageIds: Object.keys(pageEditorStates),
			hasAgentsMd: PRIMARY_AGENTS_MD_PAGE_ID in pageEditorStates,
			workspace: params.workspace,
			markdownPages: Object.keys(pageEditorStates).length,
			requestPages: Object.keys(requestPagesData).length,
			canvasPages: canvasSnapshots.length,
		});

		await reboot.registry.initializeCollabStates(reboot.context, {
			documentId: params.documentId,
			branch,
			sha,
			baseTables: {},
			// IMPORTANT: avoid crashing collab init by stringifying the metadata. Sometimes the
			// date (for reasons we do not understand) is round-tripped through protos and
			// serialized as `{}` which confuses the zod parser.
			metadataJson: Value.fromJsonString(JSON.stringify(metadata)),
			pagesProsemirrorJson: Object.fromEntries(
				Object.entries(pageEditorStates).map(([pageId, editorState]) => [
					pageId,
					Struct.fromJson(serializeForStruct(editorState)),
				])
			),
			requestPagesData,
			attachedTables: attachedTables.map((t) => ({
				table: t.table,
				primaryKey: t.primaryKey,
				rows: t.rows.map((row) => Struct.fromJson(serializeForStruct(row))),
			})),
		});

		// Upload canvas snapshots to tldraw sync server (separate from Reboot)
		if (canvasSnapshots.length > 0) {
			const { getAccessTokenSilently } = get($accessTokenFunc);
			const token = getAccessTokenSilently ? await getAccessTokenSilently() : undefined;
			const tldrawBaseUrl = getTldrawBaseUrl(env.appEnv);

			await Promise.allSettled(
				canvasSnapshots.map(async ({ pageId, snapshot }) => {
					const roomId = tldrawRoomId(
						params.workspace.slug,
						params.documentId,
						pageId,
						branch,
						sha
					);
					const uploadUrl = `${tldrawBaseUrl}/api/room/${roomId}`;

					logger.debug("Uploading canvas snapshot to tldraw server", {
						pageId,
						roomId,
					});

					const response = await fetch(uploadUrl, {
						method: "PUT",
						headers: {
							"Content-Type": "application/json",
							...(token ? { Authorization: `Bearer ${token}` } : {}),
						},
						body: JSON.stringify(snapshot),
					});

					if (!response.ok) {
						logger.error("Failed to upload canvas snapshot to tldraw server", {
							pageId,
							status: response.status,
							statusText: response.statusText,
						});
					} else {
						logger.debug("Successfully uploaded canvas snapshot", { pageId });
					}
				})
			);
		}

		// Update collab sessions rather than waiting for the listener. The write is acknowledged so
		// we're ready to go!
		const currentSessions = get($collabSessionsStore(params.workspace.slug));
		set($collabSessionsStore(params.workspace.slug), {
			...currentSessions,
			[params.documentId]: {
				sessions: {
					[branch]: { branch, sha, status: "active" },
				},
			},
		});

		// Optimistically add a remote entry to the document so the loader can find the workspace slug
		const currentDocument = get($document(params.documentId));
		if (currentDocument) {
			set($addDraftDocument, {
				...currentDocument,
				remote: {
					...currentDocument.remote,
					[params.workspace.id]: {
						id: params.documentId,
						title: metadata?.title ?? "",
						createdAt: new Date(),
						updatedAt: new Date(),
						workspace: params.workspace,
						collab: {
							[branch]: { branch, sha, status: "active" },
						},
					},
				},
			});
		}

		logger.debug("Initialized collab states", { documentId: params.documentId });
	}
);
$initializeCollab.debugLabel = `initializeCollab`;
